import { i18n } from 'next-i18next';
import { destroyCookie, parseCookies, setCookie } from 'nookies';
import { STEPS } from '@/constants/checkout';
import { COOKIES } from '@/constants/cookies';
import { ESPOTS } from '@/constants/espot';
import { FOOTER_TYPE } from '@/constants/footer';
import { HEADER_TYPE } from '@/constants/header';
import { PAGES } from '@/constants/pages';
import { STORE_ID } from '@/constants/stores';
import { setActAsCustomer } from '@/redux/slices/admin.slice';
import { connectedUserSuccess } from '@/redux/slices/auth.slice';
import { getBoxSuccess } from '@/redux/slices/box.slice';
import { getCartSuccess } from '@/redux/slices/cart.slice';
import {
	EmptyItemEspotType,
	setEmptyItemEspots,
	setIsGoogleBot,
	setIsMobileDevice,
	setIsSafari,
	setLayoutEspots,
	setNavBarMenu,
	setStoreIdForPrice,
} from '@/redux/slices/gui.slice';
import { setBaseUrl, setRatings, setStoreConf } from '@/redux/slices/root.slice';
import { addItemToStorageWBOk, getCartStorageSuccess } from '@/redux/slices/storage.slice';
import { encodeUserEmail, encodeUserPickId, getUserSuccess } from '@/redux/slices/user.slice';
import { getWishListSuccess } from '@/redux/slices/wishList.slice';
import { valueof } from '@/types';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { completePromises } from '@/utils/completePromises';

type RatingType = {
	ratingCount: number;
	ratingValue: number;
};
export const prefetchLayout = async (ctx, store, fullLayout = true) => {
	const isDebugLog = process.env.NEXT_PUBLIC_DEBUG_LOG === 'true';
	let isGoogleBotSaved: boolean;
	let storeConf: any;

	const log = (...args: any[]): void => {
		if (isDebugLog) {
			console.log(`MEP: PREF[${store.debugId}] LAYOUT`, ...args);
		}
	};

	// --- Instrumentation SSR (exploitable via CloudWatch) --------------------
	// Chaque `span('x')` retourne une fonction à appeler à la fin de l'étape.
	// Le label console.time est suffixé par un id de requête unique pour éviter
	// les collisions entre requêtes SSR concurrentes dans le même process Node.
	const _timerReqId = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`;
	const _timerStart = performance.now();
	const _spansMs: Record<string, number> = {};
	const span = (label: string): (() => void) => {
		const key = `PREF ${label} #${_timerReqId}`;
		const start = performance.now();
		console.time(key);
		return () => {
			console.timeEnd(key);
			_spansMs[label] = Math.round(performance.now() - start);
		};
	};

	try {
		const { dispatch, ajaxService, services } = store;

		const {
			productsService,
			storeService,
			authService,
			userService,
			boxService,
			cartService,
			espotService,
			storageService,
			externalService,
		} = services;

		log('ENTER', store.debugId, ctx.req.url);

		// Add a random int (3 digits) to the request
		ctx.req.reqId = parseInt(`${Math.random() * 1000}`, 10);

		// Retrieve the connected user if any
		const { actAsCustomer, authToken, baseUrl, isMobile, isSafari, isGoogleBot } = ajaxService.getContext();
		const loggedUser = authService.getConnectedUser();
		const isConnected = !isUserEmpty(loggedUser);

		isGoogleBotSaved = isGoogleBot;

		const stepHT = [
			`${PAGES.CHECKOUT}/${STEPS.REVIEW}`,
			`${PAGES.CHECKOUT}/${STEPS.PAYMODE}`,
			`${PAGES.CHECKOUT}/${STEPS.REPORT}`,
			'stripes',
			'payjunction',
			'adyen',
			'hipay',
		];

		log('CONTEXT', store.debugId, baseUrl, actAsCustomer?._id, authToken?.slice(0, 10), loggedUser?.userId);

		let storeIdForPrice = parseCookies(ctx)[COOKIES.STOREID_FOR_PRICE.NAME];

		dispatch(setIsMobileDevice({ isMobileDevice: isMobile }));
		dispatch(setIsSafari({ isSafari }));
		dispatch(setBaseUrl({ baseUrl }));
		dispatch(setIsGoogleBot({ isGoogleBot }));

		// Fetch basic info from backend
		// If no locale or equals the fallback locale => get by host
		const endStoreConf = span('storeConf');
		if (ctx.locale === ctx.defaultLocale) {
			storeConf = await storeService.getStoreConfByHost(ctx.req.headers.host, storeIdForPrice);
			if (storeConf.locale !== ctx.locale) {
				i18n?.changeLanguage(storeConf.locale);
			}
		} else if (ctx.locale.includes('-') > 0) {
			storeConf = await storeService.getStoreConfByLocale(ctx.locale, storeIdForPrice);
		} else {
			storeConf = await storeService.getStoreConfByCodeIso(ctx.locale, storeIdForPrice);
		}
		endStoreConf();

		dispatch(setStoreConf({ storeConf }));

		if (storeConf?.storeId === STORE_ID.COM && !storeIdForPrice) {
			const cookieOptions = {
				path: '/',
				maxAge: COOKIES.STOREID_FOR_PRICE.LIFETIME,
			};

			storeIdForPrice = STORE_ID.FRANCE;
			setCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME, storeIdForPrice, cookieOptions);
		}

		if (storeConf?.storeId !== STORE_ID.COM && storeIdForPrice) {
			storeIdForPrice = null;
			destroyCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME);
		}

		dispatch(setStoreIdForPrice({ storeIdForPrice }));

		// Redirect to private site authentication if needed
		if (storeConf.privateWebsite && !isConnected) {
			const currentPage = `/${ctx.resolvedUrl.split('/')[1].split('?')[0]}`;
			if (![PAGES.PRIVATE_WEBSITE, PAGES.AUTH].includes(currentPage)) {
				return {
					redirect: {
						destination: PAGES.PRIVATE_WEBSITE,
						permanent: false,
					},
				};
			}
		}

		if (isConnected) {
			dispatch(connectedUserSuccess({ user: loggedUser }));
		}

		// Set ActAs mode
		if (loggedUser?.isAdmin && actAsCustomer) {
			dispatch(setActAsCustomer({ customer: actAsCustomer }));
		}

		// Valid token detected (user or guest), get specific infos
		if (authToken) {
			const isStepHT = stepHT.some((s) => ctx.resolvedUrl.includes(s));
			const endCartBoxUser = span('cartBoxUser');
			const [cart, box, user] = await completePromises([
				cartService.get(storeConf.storeId, isStepHT, storeIdForPrice),
				boxService.get(storeConf.storeId, storeIdForPrice),
				isConnected ? userService.get(storeConf.storeId) : Promise.resolve(),
			]);
			endCartBoxUser();
			if (cart) {
				if (!storeIdForPrice && cart.storeIdForPrice) {
					const cookieOptions = {
						path: '/',
						maxAge: COOKIES.STOREID_FOR_PRICE.LIFETIME,
					};

					setCookie(ctx, COOKIES.STOREID_FOR_PRICE.NAME, cart.storeIdForPrice, cookieOptions);
				}
				dispatch(getCartSuccess({ content: cart }));
			}
			if (box) {
				dispatch(getBoxSuccess({ content: box }));
			}
			if (user) {
				dispatch(getUserSuccess({ user }));
				if (user.hasStorage) {
					const endCartStorage = span('cartStorage');
					const cartStorage: any = await storageService.getCartStorage();
					endCartStorage();
					dispatch(getCartStorageSuccess({ cartStorage }));
				}
				if (user.wineBoxStorage) {
					dispatch(addItemToStorageWBOk({ storageWineBox: user.wineBoxStorage }));
				}
				if (user.wishList) {
					dispatch(getWishListSuccess({ products: user.wishList }));
				}
				if (user.email) {
					const { createHash } = await import('crypto');
					const md5 = createHash('md5').update(user.email).digest('hex');
					const sha256 = createHash('sha256').update(user.email).digest('hex');
					dispatch(encodeUserEmail({ md5, sha256 }));

					if (user.pickId) {
						const hashedPickId = createHash('sha256').update(user.pickId).digest('hex');
						dispatch(encodeUserPickId(hashedPickId));
					}
				}
			}
		}

		if (fullLayout) {
			// Full layout requested, else don't retrive menu and espots
			const espots: valueof<typeof ESPOTS>[] = [ESPOTS.HEADER_PROMOTION, ESPOTS.CART_SECURE_INFO, ESPOTS.FOOTER_LEGAL_NOTICE];

			const endRating = span('rating');
			const rating: RatingType = await externalService.getGlobalReview(storeConf.storeId);
			endRating();

			if (rating) {
				const ratingValue = rating?.ratingValue;
				const ratingCount = rating?.ratingCount;
				dispatch(setRatings({ ratingValue, ratingCount }));
			}

			const endMenuEspots = span('menuEspots');
			const [menu, [promo, secureInfo, legalNotice]] = await completePromises([
				productsService.getMenu(storeConf.storeId),
				espotService.getEspotDataByLocations(storeConf.storeId, espots),
			]);
			endMenuEspots();

			const endEmptyItemEspot = span('emptyItemEspot');
			const emptyItemEspots = (await espotService.getEspotByTypeAndStoreId(
				'ESPOT_EMPTY_ITEM',
				storeConf.storeId,
			)) satisfies EmptyItemEspotType[];
			endEmptyItemEspot();

			dispatch(setNavBarMenu({ menu }));
			dispatch(setLayoutEspots({ espots: { promo, secureInfo, legalNotice } }));
			dispatch(setEmptyItemEspots({ emptyItemEspots }));
		}

		_spansMs.total = Math.round(performance.now() - _timerStart);

		// Server-Timing : expose les spans du layout au navigateur (RUM) pour corréler
		// au TTFB client. NB : ne couvre que la phase layout, pas le rendu React (qui a
		// lieu après getServerSideProps) — pour le temps serveur complet, voir l'ALB
		// TargetResponseTime ou un `$upstream_response_time` ajouté côté nginx.
		if (ctx.res && !ctx.res.headersSent) {
			ctx.res.setHeader(
				'Server-Timing',
				Object.entries(_spansMs)
					.map(([k, v]) => `${k};dur=${v}`)
					.join(', '),
			);
		}

		// `process.stdout.write` (et non `console.log`) car `compiler.removeConsole`
		// dans next.config.js supprime tous les `console.*` du bundle en prod.
		process.stdout.write(
			`${JSON.stringify({
				tag: 'PREF_LAYOUT_TIMING',
				reqId: _timerReqId,
				url: ctx.req?.url,
				host: ctx.req?.headers?.host,
				country: ctx.req?.headers?.['cloudfront-viewer-country'],
				locale: ctx.locale,
				storeId: storeConf?.storeId,
				connected: isConnected,
				hasToken: !!authToken,
				isGoogleBot: isGoogleBotSaved,
				fullLayout,
				spansMs: _spansMs,
			})}\n`,
		);

		log('EXIT', store.debugId, ctx.req?.url);
	} catch (error) {
		log('ERROR !!!!', store.debugId, ctx.req?.url);
		console.error('prefetchLayout error', error);
		_spansMs.total = Math.round(performance.now() - _timerStart);
		process.stdout.write(
			`${JSON.stringify({
				tag: 'PREF_LAYOUT_TIMING',
				reqId: _timerReqId,
				url: ctx.req?.url,
				host: ctx.req?.headers?.host,
				country: ctx.req?.headers?.['cloudfront-viewer-country'],
				locale: ctx.locale,
				isGoogleBot: isGoogleBotSaved,
				error: true,
				spansMs: _spansMs,
			})}\n`,
		);
	}

	// Return the default page layout config
	return {
		locale: storeConf.locale || 'fr-FR',
		withPromotionHeader: true,
		withHeader: true,
		headerType: HEADER_TYPE.DEFAULT,
		withNavBar: true,
		withFooter: true,
		footerType: FOOTER_TYPE.DEFAULT,
		withScrollButton: false,
		withWineBoxModal: true,
		isGoogleBot: isGoogleBotSaved,
	};
};
